import * as Yup from "yup";
import { LlmRequestError } from "./llmClient.js";

// ── LLM output contract ──────────────────────────────────────────────────────
const llmQuestionSchema = Yup.object().shape({
  number: Yup.number()
    .transform((value, original) =>
      original === "" || original == null
        ? undefined
        : Number(original)
    )
    .typeError("question number must be numeric")
    .required(),
  body: Yup.string().trim().required(),
  image_ids: Yup.array().of(Yup.string()).default([]),
  options: Yup.array()
    .of(
      Yup.object().shape({
        text: Yup.string().default(""),
        correct: Yup.boolean().default(false),
        image_id: Yup.string().nullable().default(null)
      })
    )
    .min(2)
    .required(),
  explanation: Yup.string().default(""),
  answer_source: Yup.string()
    .oneOf(["explicit", "inferred", "unclear"])
    .default("explicit")
});

const llmResponseSchema = Yup.object().shape({
  questions: Yup.array().of(llmQuestionSchema).required()
});

/**
 * Validate + coerce one LLM chunk reply into question objects.
 * @throws {LlmRequestError} when the reply does not match the contract.
 */
export function parseLlmQuestions(replyJson) {
  try {
    const value = llmResponseSchema.validateSync(replyJson, {
      abortEarly: false,
      stripUnknown: true
    });
    return value.questions;
  } catch (error) {
    const details = (error.errors || [error.message])
      .slice(0, 5)
      .join("; ");
    throw new LlmRequestError(
      `The LLM reply did not match the expected format: ${details}`
    );
  }
}

// ── Normalization into FlashCardForm entries ─────────────────────────────────
// Mirrors flashCardInitialObj (src/containers/FlashCards/FlashCardQuestions/
// validation.js); duplicated here to keep utils independent of containers.

const emptyOption = () => ({
  content: "",
  currentTab: "text",
  flashcard_attachments_attributes: [],
  correct: false
});

const emptyFlashcard = () => ({
  question_body: "",
  mode_eligibility: "",
  flashcard_attachments_attributes: [],
  difficulty_level: "",
  flashcard_sub_topic_id: null,
  flashcard_topic_id: null,
  flashcard_options_attributes: [emptyOption(), emptyOption()],
  flashcard_explanation_attributes: {
    body: "",
    flashcard_attachments_attributes: []
  }
});

const attachmentFor = (image) =>
  image
    ? { path: { file: image.file, url: image.url }, position: 0 }
    : null;

/**
 * Convert validated LLM questions into flashCardInitialObj-shaped entries the
 * Add form can absorb directly (images as {path:{file,url}, position} blobs so
 * the existing submit-time S3 presign upload keeps working unchanged).
 *
 * Tab rule (uniform per question, matching FlashCardAnswerForm + yup schema):
 * all options have images and no text -> "image"; all have images and text ->
 * "text_image"; otherwise "text" (leftover images stay attached but only text
 * is required, so validation never blocks on a partially-image question).
 */
export function toFormikFlashcards(questions, imagesById) {
  return questions.map((question) => {
    const entry = emptyFlashcard();

    entry.question_body = question.body;

    const stemAttachments = (question.image_ids || [])
      .map((id) => imagesById[id])
      .filter(Boolean)
      .map((image, position) => ({
        path: { file: image.file, url: image.url },
        position
      }));
    entry.flashcard_attachments_attributes = stemAttachments;

    const options = question.options.map((option) => {
      const converted = emptyOption();
      converted.content = (option.text || "").trim();
      converted.correct = Boolean(option.correct);
      const image = imagesById[option.image_id];
      if (image) {
        converted.flashcard_attachments_attributes = [
          attachmentFor(image)
        ];
      }
      return converted;
    });

    while (options.length < 2) options.push(emptyOption());
    entry.flashcard_options_attributes = options;

    const activeOptions = options;
    const allHaveImages = activeOptions.every(
      (option) => option.flashcard_attachments_attributes.length
    );
    const anyText = activeOptions.some(
      (option) => option.content.trim() !== ""
    );
    entry.flashcard_options_attributes.forEach((option) => {
      option.currentTab = allHaveImages
        ? anyText
          ? "text_image"
          : "image"
        : "text";
    });

    // exactly one correct: keep the first flagged option
    const firstCorrect = options.find((option) => option.correct);
    options.forEach((option) => {
      option.correct = option === firstCorrect;
    });

    entry.flashcard_explanation_attributes.body =
      question.explanation || "";

    entry.tempId = crypto.randomUUID();
    entry.answer_source = question.answer_source;
    return entry;
  });
}
